import type {
  CustomerFieldKey,
  CustomerFieldSelectors,
  DetectedCustomerFields,
} from './types';

type FieldElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

type FieldHeuristics = {
  ids: string[];
  names: string[];
  autocompletes: string[];
  types: string[];
  placeholders: string[];
  ariaLabels: string[];
  labelTexts: string[];
  dataAttributes: string[];
};

const FIELD_HEURISTICS: Record<CustomerFieldKey, FieldHeuristics> = {
  email: {
    ids: ['email', 'customer_email', 'billing_email', 'shipping_email'],
    names: [
      'email',
      'customer[email]',
      'billing[email]',
      'shipping[email]',
      'contact[email]',
    ],
    autocompletes: ['email'],
    types: ['email'],
    placeholders: ['email', 'e-mail', 'correo'],
    ariaLabels: ['email', 'e-mail'],
    labelTexts: ['email', 'e-mail', 'correo eletrônico', 'correo electronico'],
    dataAttributes: ['email', 'customer-email'],
  },
  phone: {
    ids: ['phone', 'telephone', 'tel', 'customer_phone', 'billing_phone'],
    names: [
      'phone',
      'telephone',
      'tel',
      'customer[phone]',
      'billing[phone]',
      'shipping[phone]',
      'contact[phone]',
    ],
    autocompletes: ['tel', 'tel-national', 'tel-local'],
    types: ['tel'],
    placeholders: ['phone', 'telefone', 'celular', 'mobile', 'whatsapp'],
    ariaLabels: ['phone', 'telefone', 'telephone'],
    labelTexts: ['phone', 'telefone', 'celular', 'mobile', 'whatsapp'],
    dataAttributes: ['phone', 'telephone', 'customer-phone'],
  },
  firstName: {
    ids: ['firstname', 'first_name', 'first-name', 'givenname', 'given_name'],
    names: [
      'firstName',
      'first_name',
      'firstname',
      'customer[first_name]',
      'billing[first_name]',
      'shipping[first_name]',
      'given-name',
    ],
    autocompletes: ['given-name', 'fname'],
    types: ['text'],
    placeholders: ['first name', 'nome', 'primeiro nome'],
    ariaLabels: ['first name', 'nome', 'primeiro nome'],
    labelTexts: ['first name', 'nome', 'primeiro nome', 'given name'],
    dataAttributes: ['first-name', 'firstname'],
  },
  lastName: {
    ids: ['lastname', 'last_name', 'last-name', 'familyname', 'family_name'],
    names: [
      'lastName',
      'last_name',
      'lastname',
      'customer[last_name]',
      'billing[last_name]',
      'shipping[last_name]',
      'family-name',
    ],
    autocompletes: ['family-name', 'lname'],
    types: ['text'],
    placeholders: ['last name', 'sobrenome', 'apelido'],
    ariaLabels: ['last name', 'sobrenome'],
    labelTexts: ['last name', 'sobrenome', 'family name', 'surname'],
    dataAttributes: ['last-name', 'lastname'],
  },
  address1: {
    ids: ['address', 'address1', 'address_1', 'street', 'street_address'],
    names: [
      'address',
      'address1',
      'address_1',
      'street',
      'street_address',
      'customer[address1]',
      'billing[address1]',
      'shipping[address1]',
      'billing[address]',
    ],
    autocompletes: ['street-address', 'address-line1'],
    types: ['text'],
    placeholders: ['address', 'endereço', 'endereco', 'street'],
    ariaLabels: ['address', 'endereço', 'street'],
    labelTexts: ['address', 'endereço', 'endereco', 'street address'],
    dataAttributes: ['address', 'address1', 'street-address'],
  },
  address2: {
    ids: ['address2', 'address_2', 'complement', 'address_line_2'],
    names: [
      'address2',
      'address_2',
      'complement',
      'customer[address2]',
      'billing[address2]',
      'shipping[address2]',
    ],
    autocompletes: ['address-line2'],
    types: ['text'],
    placeholders: ['complement', 'complemento', 'apartment', 'apt'],
    ariaLabels: ['complement', 'complemento', 'address line 2'],
    labelTexts: ['complement', 'complemento', 'address line 2', 'apartment'],
    dataAttributes: ['address2', 'address-line2'],
  },
  city: {
    ids: ['city', 'locality', 'town'],
    names: [
      'city',
      'locality',
      'customer[city]',
      'billing[city]',
      'shipping[city]',
    ],
    autocompletes: ['address-level2'],
    types: ['text'],
    placeholders: ['city', 'cidade'],
    ariaLabels: ['city', 'cidade'],
    labelTexts: ['city', 'cidade'],
    dataAttributes: ['city'],
  },
  state: {
    ids: ['state', 'province', 'region', 'uf'],
    names: [
      'state',
      'province',
      'region',
      'customer[state]',
      'billing[state]',
      'shipping[state]',
    ],
    autocompletes: ['address-level1'],
    types: ['text'],
    placeholders: ['state', 'estado', 'province', 'uf'],
    ariaLabels: ['state', 'estado', 'province'],
    labelTexts: ['state', 'estado', 'province', 'uf'],
    dataAttributes: ['state', 'province'],
  },
  postalCode: {
    ids: [
      'postal',
      'postal_code',
      'postalcode',
      'zip',
      'zipcode',
      'zip_code',
      'cep',
    ],
    names: [
      'postalCode',
      'postal_code',
      'postal',
      'zip',
      'zipcode',
      'zip_code',
      'customer[postal_code]',
      'billing[postal_code]',
      'shipping[postal_code]',
      'cep',
    ],
    autocompletes: ['postal-code'],
    types: ['text'],
    placeholders: ['postal code', 'zip', 'zip code', 'cep'],
    ariaLabels: ['postal code', 'zip', 'cep'],
    labelTexts: ['postal code', 'zip', 'zip code', 'cep', 'código postal'],
    dataAttributes: ['postal-code', 'zip', 'cep'],
  },
  country: {
    ids: ['country', 'country_code', 'countrycode'],
    names: [
      'country',
      'country_code',
      'customer[country]',
      'billing[country]',
      'shipping[country]',
    ],
    autocompletes: ['country', 'country-name'],
    types: ['text'],
    placeholders: ['country', 'país', 'pais'],
    ariaLabels: ['country', 'país', 'pais'],
    labelTexts: ['country', 'país', 'pais'],
    dataAttributes: ['country'],
  },
};

const HEURISTIC_SCORES = {
  customSelector: 100,
  autocomplete: 95,
  id: 90,
  name: 85,
  dataAttribute: 80,
  ariaLabel: 75,
  placeholder: 70,
  label: 65,
  type: 40,
} as const;

function normalizeText(value: string): string {
  return value.trim().toLowerCase();
}

function isVisibleField(el: FieldElement): boolean {
  if (el instanceof HTMLInputElement && el.type === 'hidden') return false;
  if ('disabled' in el && el.disabled) return false;
  return true;
}

function matchesPattern(text: string, patterns: string[]): boolean {
  const normalized = normalizeText(text);
  return patterns.some(pattern => normalized.includes(normalizeText(pattern)));
}

function querySelectorField(selector: string): FieldElement | null {
  const el = document.querySelector(selector);
  if (
    (el instanceof HTMLInputElement ||
      el instanceof HTMLSelectElement ||
      el instanceof HTMLTextAreaElement) &&
    isVisibleField(el)
  ) {
    return el;
  }
  return null;
}

function findById(ids: string[]): FieldElement | null {
  for (const id of ids) {
    const el = document.getElementById(id);
    if (
      (el instanceof HTMLInputElement ||
        el instanceof HTMLSelectElement ||
        el instanceof HTMLTextAreaElement) &&
      isVisibleField(el)
    ) {
      return el;
    }
  }
  return null;
}

function findByName(names: string[]): FieldElement | null {
  for (const name of names) {
    const elements = document.querySelectorAll(
      `input[name="${name}"], select[name="${name}"], textarea[name="${name}"]`
    );
    for (const el of elements) {
      if (
        (el instanceof HTMLInputElement ||
          el instanceof HTMLSelectElement ||
          el instanceof HTMLTextAreaElement) &&
        isVisibleField(el)
      ) {
        return el;
      }
    }
  }
  return null;
}

function findByAutocomplete(values: string[]): FieldElement | null {
  for (const value of values) {
    const elements = document.querySelectorAll(
      `input[autocomplete="${value}"], select[autocomplete="${value}"], textarea[autocomplete="${value}"]`
    );
    for (const el of elements) {
      if (
        (el instanceof HTMLInputElement ||
          el instanceof HTMLSelectElement ||
          el instanceof HTMLTextAreaElement) &&
        isVisibleField(el)
      ) {
        return el;
      }
    }
  }
  return null;
}

function findByType(types: string[]): FieldElement | null {
  for (const type of types) {
    const elements = document.querySelectorAll(`input[type="${type}"]`);
    for (const el of elements) {
      if (el instanceof HTMLInputElement && isVisibleField(el)) {
        return el;
      }
    }
  }
  return null;
}

function findByDataAttributes(attributes: string[]): FieldElement | null {
  for (const attr of attributes) {
    const elements = document.querySelectorAll(
      `[data-field="${attr}"], [data-customer="${attr}"], [data-name="${attr}"]`
    );
    for (const el of elements) {
      if (
        (el instanceof HTMLInputElement ||
          el instanceof HTMLSelectElement ||
          el instanceof HTMLTextAreaElement) &&
        isVisibleField(el)
      ) {
        return el;
      }
    }
  }
  return null;
}

function findByPlaceholder(patterns: string[]): FieldElement | null {
  const elements = document.querySelectorAll(
    'input[placeholder], textarea[placeholder]'
  );
  for (const el of elements) {
    if (
      !(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) ||
      !isVisibleField(el)
    ) {
      continue;
    }
    const placeholder = el.getAttribute('placeholder') ?? '';
    if (matchesPattern(placeholder, patterns)) return el;
  }
  return null;
}

function findByAriaLabel(patterns: string[]): FieldElement | null {
  const elements = document.querySelectorAll('[aria-label]');
  for (const el of elements) {
    if (
      !(
        el instanceof HTMLInputElement ||
        el instanceof HTMLSelectElement ||
        el instanceof HTMLTextAreaElement
      ) ||
      !isVisibleField(el)
    ) {
      continue;
    }
    const ariaLabel = el.getAttribute('aria-label') ?? '';
    if (matchesPattern(ariaLabel, patterns)) return el;
  }
  return null;
}

function findByLabelTexts(patterns: string[]): FieldElement | null {
  const labels = document.querySelectorAll('label');
  for (const label of labels) {
    const text = label.textContent ?? '';
    if (!matchesPattern(text, patterns)) continue;

    const forId = label.getAttribute('for');
    if (forId) {
      const el = document.getElementById(forId);
      if (
        (el instanceof HTMLInputElement ||
          el instanceof HTMLSelectElement ||
          el instanceof HTMLTextAreaElement) &&
        isVisibleField(el)
      ) {
        return el;
      }
    }

    const nested = label.querySelector('input, select, textarea');
    if (
      (nested instanceof HTMLInputElement ||
        nested instanceof HTMLSelectElement ||
        nested instanceof HTMLTextAreaElement) &&
      isVisibleField(nested)
    ) {
      return nested;
    }
  }
  return null;
}

interface ScoredCandidate {
  element: FieldElement;
  score: number;
}

function scoreField(
  key: CustomerFieldKey,
  customSelector?: string
): FieldElement | null {
  const heuristics = FIELD_HEURISTICS[key];
  const candidates: ScoredCandidate[] = [];

  const addCandidate = (element: FieldElement | null, score: number): void => {
    if (!element) return;
    const existing = candidates.find(c => c.element === element);
    if (existing) {
      existing.score = Math.max(existing.score, score);
      return;
    }
    candidates.push({ element, score });
  };

  if (customSelector) {
    addCandidate(
      querySelectorField(customSelector),
      HEURISTIC_SCORES.customSelector
    );
  }

  addCandidate(
    findByAutocomplete(heuristics.autocompletes),
    HEURISTIC_SCORES.autocomplete
  );
  addCandidate(findByType(heuristics.types), HEURISTIC_SCORES.type);
  addCandidate(findById(heuristics.ids), HEURISTIC_SCORES.id);
  addCandidate(findByName(heuristics.names), HEURISTIC_SCORES.name);
  addCandidate(
    findByDataAttributes(heuristics.dataAttributes),
    HEURISTIC_SCORES.dataAttribute
  );
  addCandidate(
    findByAriaLabel(heuristics.ariaLabels),
    HEURISTIC_SCORES.ariaLabel
  );
  addCandidate(
    findByPlaceholder(heuristics.placeholders),
    HEURISTIC_SCORES.placeholder
  );
  addCandidate(findByLabelTexts(heuristics.labelTexts), HEURISTIC_SCORES.label);

  if (candidates.length === 0) return null;

  candidates.sort((a, b) => b.score - a.score);
  return candidates[0].element;
}

export function detectCustomerFields(
  customSelectors?: CustomerFieldSelectors
): DetectedCustomerFields {
  const keys = Object.keys(FIELD_HEURISTICS) as CustomerFieldKey[];
  const result = {} as DetectedCustomerFields;

  for (const key of keys) {
    result[key] = scoreField(key, customSelectors?.[key]);
  }

  return result;
}

export function readFieldValue(
  element: FieldElement | null
): string | undefined {
  if (!element) return undefined;
  const value = element.value?.trim();
  return value || undefined;
}

export function collectCustomerFieldInputs(
  fields: DetectedCustomerFields
): FieldElement[] {
  const inputs: FieldElement[] = [];
  const seen = new Set<FieldElement>();

  for (const field of Object.values(fields)) {
    if (field && !seen.has(field)) {
      seen.add(field);
      inputs.push(field);
    }
  }

  return inputs;
}

export function syncDetectedFieldsToStore(
  fields: DetectedCustomerFields,
  onField: (key: CustomerFieldKey, value: string) => void
): void {
  for (const key of Object.keys(fields) as CustomerFieldKey[]) {
    const value = readFieldValue(fields[key]);
    if (value) onField(key, value);
  }
}
